import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    HStack,
    VStack,
    Text,
    Select,
    Switch,
    Input,
    Badge,
    Tag,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    IconButton,
    useToast,
    Grid,
    Divider,
    Spinner,
    Center,
    FormControl,
    FormLabel
} from '@chakra-ui/react';
import {
    FiClock,
    FiRefreshCw,
    FiDollarSign,
    FiPlus,
    FiX,
    FiUsers,
    FiTrendingUp,
    FiAlertCircle,
    FiArrowLeft,
    FiInfo,
} from 'react-icons/fi';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import API from '../../helpers/axiosInstance';
import { getApiHeaders, hasPermission } from '../../utils/DefaultFunction';

// ── helpers ────────────────────────────────────────────────────

const getCustomerId = () => {
    try { return JSON.parse(localStorage.getItem('customer'))?.customer_id; } catch { return null; }
};

const getRfmHeaders = () => ({
    ...getApiHeaders(),
    'x-customer-id': getCustomerId(),
});

const operatorToApi   = (op) => ({ '≥': '>=', '≤': '<=', '=': '==' }[op] || op);
const operatorFromApi = (op) => ({ '>=': '≥', '<=': '≤', '==': '=' }[op] || op);
const countTypeToApi   = (ct) => ct === 'total' ? 'total_events' : ct === 'unique' ? 'unique_days' : ct;
const countTypeFromApi = (ct) => ct === 'total_events' ? 'total' : ct === 'unique_days' ? 'unique' : ct;

const chakraColorToHex = (color) => {
    if (!color || color.startsWith('#')) return color || '#6B7280';
    return ({
        'green.500':  '#22C55E',
        'blue.500':   '#3B82F6',
        'yellow.500': '#EAB308',
        'red.500':    '#EF4444',
        'orange.500': '#F97316',
        'purple.500': '#A855F7',
    })[color] || '#6B7280';
};

/**
 * RFM_008 / RFM_009: bucket bounds are held as digit STRINGS ('' = blank), not numbers.
 *
 * - '' is the only "open-ended" marker. The old code used 999 as that sentinel, and the
 *   input rendered blank for anything >= 999 — so typing 1000 wiped the field and nothing
 *   above 998 could be entered.
 * - Strings also keep the controlled input in step with the DOM: with numbers, typing a
 *   second '0' parsed to the same 0, React saw no change and left "00" on screen.
 */
const MAX_BUCKET_DIGITS = 9;
const MAX_BUCKET_VALUE  = 999999999;

// Digits only: strips +, -, e, '.', spaces and anything else, drops leading zeros,
// and enforces the digit/value ceiling. '++123' -> '123', '12-34' -> '1234', '0000' -> '0'.
const sanitizeBucketValue = (raw) => {
    const digits = String(raw ?? '').replace(/\D+/g, '');
    if (!digits) return '';
    const trimmed = digits.replace(/^0+(?=\d)/, '').slice(0, MAX_BUCKET_DIGITS);
    return String(Math.min(Number(trimmed), MAX_BUCKET_VALUE));
};

const bucketsToApi = (ranges) =>
    ranges
        .filter(r => r.score !== '' && r.min !== '' && r.min !== undefined)
        .map(r => ({
            score: Number(r.score),
            from:  Number(r.min),
            // blank "Till" is the open-ended bucket
            to:    r.max === '' || r.max === undefined || r.max === null ? null : Number(r.max),
        }));

const bucketsFromApi = (apiRanges) =>
    (apiRanges || []).map(r => ({
        score: r.score,
        min:   r.from === null || r.from === undefined ? '' : String(r.from),
        max:   r.to === null || r.to === undefined ? '' : String(r.to),
    }));

// ── fixed segments ─────────────────────────────────────────────

const FIXED_SEGMENTS = [
    { name: 'Champions', color: '#22C55E' },
    { name: 'Loyal',     color: '#3B82F6' },
    { name: 'At Risk',   color: '#EAB308' },
    { name: 'Lost',      color: '#EF4444' },
];

const defaultSegmentRule = () => ({
    recencyOperator: '≥', recencyValue: 3,
    frequencyOperator: '≥', frequencyValue: 3,
    monetaryOperator: '≥', monetaryValue: 3,
});

const initSegments = () =>
    FIXED_SEGMENTS.map(s => ({ name: s.name, color: s.color, enabled: true, rule: defaultSegmentRule() }));

const DEFAULT_SCORE_RANGES = [5, 4, 3, 2, 1].map(score => ({ score, min: '', max: '' }));

const mergeScoreRanges = (apiBuckets) => {
    const fromApi = bucketsFromApi(apiBuckets || []);
    return DEFAULT_SCORE_RANGES.map(def => {
        const match = fromApi.find(r => r.score === def.score);
        return match ? { ...match } : { ...def };
    });
};

// ── component ──────────────────────────────────────────────────

/**
 * RFM_005: these three lived INSIDE the RFMConfiguration component body, so every
 * keystroke produced a brand-new component TYPE. React cannot reconcile a changed type —
 * it unmounts the old subtree and mounts a fresh one — so each scoring-bucket <Input> was
 * a new DOM node on every render and lost focus after a single digit. At module scope the
 * type is stable and the inputs keep focus.
 *
 * updateBucket comes along because ScoreRangeTable calls it; it is pure (the setter is
 * passed in), so it needs nothing from the component closure.
 */
// ── bucket helpers ──────────────────────────────────────────
const updateBucket = (setFn, index, field, rawValue) => {
    setFn(prev => {
        const updated = [...prev];
        updated[index] = {
            ...updated[index],
            [field]: field === 'score'
                ? (rawValue === '' ? '' : parseInt(rawValue))
                : sanitizeBucketValue(rawValue),
        };
        return updated;
    });
};

// ── sub-components ──────────────────────────────────────────
const EventItem = ({ event, onRemove }) => (
    <Flex align="center" justify="space-between" p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
        <Box>
            <Text fontSize="sm" fontWeight="medium">{event.displayName}</Text>
            <HStack mt={1} spacing={1}>
                {(event.tags || []).map(tag => <Badge key={tag} variant="outline" fontSize="xs">{tag}</Badge>)}
                {event.property && <Text fontSize="xs" color="gray.500">→ property: {event.property}</Text>}
            </HStack>
        </Box>
        <IconButton icon={<FiX />} size="xs" variant="ghost" colorScheme="red" aria-label="Remove" onClick={onRemove} />
    </Flex>
);

const ScoreRangeTable = ({ ranges, setRanges, colorScheme }) => (
    <Box>
        <Grid templateColumns="52px 100px 100px" gap={2} pb={2} borderBottom="1px solid" borderColor="gray.200" mb={2}>
            <Text fontSize="xs" fontWeight="medium" color="gray.500" textAlign="center">Score</Text>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">From</Text>
            <Text fontSize="xs" fontWeight="medium" color="gray.500">Till (blank = ∞)</Text>
        </Grid>
        {ranges.map((range, i) => {
            // "Till" below "From" cannot describe a bucket — flag it without blocking typing
            const invertedRange =
                range.min !== '' && range.max !== '' && Number(range.max) < Number(range.min);
            return (
                <Grid key={range.score} templateColumns="52px 100px 100px" gap={2} mb={2} alignItems="center">
                    <Flex justify="center">
                        <Tag
                            bg={colorScheme}
                            variant="subtle"
                            fontSize="sm"
                            px={6}
                            py={2}
                            borderRadius="md"
                        >
                            {range.score}
                        </Tag>
                    </Flex>
                    {/* type="text" + inputMode: a number input keeps invalid text like "++123"
                        on screen while reporting value '' , so the field looked like it accepted
                        it. As text every keystroke is visible and sanitizeBucketValue strips it. */}
                    <Input
                        type="text"
                        inputMode="numeric"
                        size="sm"
                        placeholder="0"
                        maxLength={MAX_BUCKET_DIGITS}
                        value={range.min}
                        onChange={e => updateBucket(setRanges, i, 'min', e.target.value)}
                    />
                    <Input
                        type="text"
                        inputMode="numeric"
                        size="sm"
                        placeholder="∞"
                        maxLength={MAX_BUCKET_DIGITS}
                        isInvalid={invertedRange}
                        value={range.max}
                        onChange={e => updateBucket(setRanges, i, 'max', e.target.value)}
                    />
                </Grid>
            );
        })}
    </Box>
);

const RFMConfiguration = ({ header }) => {
    const navigate  = useNavigate();
    const toast     = useToast();
    const { id: routeId } = useParams();

    const location = useLocation();
    const [featurePermissions, setFeaturePermissions] = useState([]);
    const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true);
    // Backend groups RFM permissions under the list page's own feature slug,
    // not this configure page's route (which includes the model id, or "new").
    const featurePermParams = "marketing/rfm-models";

    const fetchFeaturePermissions = async () => {
        setFeaturePermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${featurePermParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setFeaturePermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setFeaturePermissions([]);
        } finally {
            setFeaturePermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchFeaturePermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rfmModelConfigurePermission = hasPermission("rfm-model-configure", featurePermissions);

    // ── available events (from API) ─────────────────────────────
    const [availableEvents,     setAvailableEvents]     = useState([]);
    const [loadingEvents,       setLoadingEvents]       = useState(false);
    const [eventPropertiesCache, setEventPropertiesCache] = useState({});
    const [loadingProperties,   setLoadingProperties]   = useState(false);

    // ── model meta ──────────────────────────────────────────────
    const [rfmId,           setRfmId]           = useState(null);
    const [modelName,       setModelName]       = useState('');
    const [isEnabled,       setIsEnabled]       = useState(true);
    const [refreshFrequency, setRefreshFrequency] = useState('daily');

    // ── event config (Tab 1) ────────────────────────────────────
    const [recencyEvents,   setRecencyEvents]   = useState([]);
    const [frequencyEvents, setFrequencyEvents] = useState([]);
    const [monetaryEvents,  setMonetaryEvents]  = useState([]);
    const [frequencyCountType, setFrequencyCountType] = useState('');
    const [monetaryAggregation, setMonetaryAggregation] = useState('');
    const [loadedConfig, setLoadedConfig] = useState(null); // raw config for re-mapping after events load

    // ── rule config (Tab 2) ─────────────────────────────────────
    const [recencyWindow,   setRecencyWindow]   = useState(null);
    const [frequencyWindow, setFrequencyWindow] = useState(null);
    const [monetaryWindow,  setMonetaryWindow]  = useState(null);

    // ── scoring model (Tab 3) — fixed 5 rows ───────────────────
    const [recencyRanges,   setRecencyRanges]   = useState(() => DEFAULT_SCORE_RANGES.map(r => ({...r})));
    const [frequencyRanges, setFrequencyRanges] = useState(() => DEFAULT_SCORE_RANGES.map(r => ({...r})));
    const [monetaryRanges,  setMonetaryRanges]  = useState(() => DEFAULT_SCORE_RANGES.map(r => ({...r})));

    // ── segment mapping (Tab 4) ─────────────────────────────────
    const [segments, setSegments] = useState(initSegments);

    // ── ui state ────────────────────────────────────────────────
    const [configLoading, setConfigLoading] = useState(true);
    const [tabSaving,     setTabSaving]     = useState({});
    const [fullSaving,    setFullSaving]    = useState(false);
    const [activeTab,     setActiveTab]     = useState(0);

    /**
     * Which tabs the user has actually edited.
     *
     * RFM_004 asks that only explicitly-configured sections be saved, and RFM_006 was caused
     * by the opposite problem: "Save Configuration" only ever saved the ACTIVE tab, so a
     * Frequency choice made on the Rule tab was silently dropped if the user switched tabs
     * before saving. Tracking edits lets handleSaveAll save every touched tab and skip the
     * untouched ones.
     */
    const [dirtyTabs, setDirtyTabs] = useState({});
    const markDirty = (tab) => setDirtyTabs(prev => (prev[tab] ? prev : { ...prev, [tab]: true }));

    /**
     * Wraps a state setter so using the control also flags its tab as edited. Passes the
     * argument straight through, so both `set(value)` and `set(prev => …)` still work —
     * ScoreRangeTable/updateBucket rely on the updater form.
     */
    const dirty = (tab, setFn) => (value) => { markDirty(tab); setFn(value); };

    // monetary event picker
    const [selectedMonetaryEventName, setSelectedMonetaryEventName] = useState('');
    const [selectedMonetaryProperty,  setSelectedMonetaryProperty]  = useState('');

    // ── fetch events list ───────────────────────────────────────
    const fetchUniqueEvents = async () => {
        setLoadingEvents(true);
        try {
            const res = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-events`,
                {},
                { headers: getApiHeaders() }
            );
            if (res.data?.status) {
                setAvailableEvents(
                    (res.data.data.events || []).map(ev => ({
                        id: ev.name,
                        name: ev.name,
                        displayName: ev.display_name || ev.name,
                        tags: [],
                    }))
                );
            }
        } catch (e) {
            console.error('Failed to fetch events:', e);
        } finally {
            setLoadingEvents(false);
        }
    };

    // ── fetch properties for a monetary event ───────────────────
    const fetchEventProperties = async (eventName) => {
        if (!eventName || eventPropertiesCache[eventName] !== undefined) return;
        setLoadingProperties(true);
        try {
            const res = await API.post(
                `${process.env.REACT_APP_API_URL}/fetch-unique-attributes`,
                { event: eventName },
                { headers: getApiHeaders() }
            );
            const props = res.data?.status
                ? (res.data.data.attributes || []).map(a => a.name)
                : [];
            setEventPropertiesCache(prev => ({ ...prev, [eventName]: props }));
        } catch {
            setEventPropertiesCache(prev => ({ ...prev, [eventName]: [] }));
        } finally {
            setLoadingProperties(false);
        }
    };

    // ── populate state from loaded config (edit mode) ───────────
    const loadConfigFromApi = (config) => {
        if (!config) return;

        const findEvent = (name) =>
            availableEvents.find(e => e.name === name) || { id: name, name, displayName: name, tags: [] };

        if (config._id)   setRfmId(config._id);
        if (config.name)  setModelName(config.name);
        if (typeof config.is_enabled !== 'undefined') setIsEnabled(config.is_enabled);
        if (config.refresh_frequency) setRefreshFrequency(config.refresh_frequency);

        // events
        if (config.recency?.events?.length)
            setRecencyEvents(config.recency.events.map(findEvent));
        if (config.frequency?.events?.length)
            setFrequencyEvents(config.frequency.events.map(findEvent));
        if (config.monetary?.events?.length) {
            setMonetaryEvents(config.monetary.events.map(ev => ({ ...findEvent(ev.event), property: ev.property })));
            config.monetary.events.forEach(ev => fetchEventProperties(ev.event));
        }
        if (config.frequency?.count_type)    setFrequencyCountType(countTypeFromApi(config.frequency.count_type));
        if (config.monetary?.aggregation)    setMonetaryAggregation(config.monetary.aggregation);

        // rule config
        if (config.recency?.time_window_days)   setRecencyWindow(config.recency.time_window_days);
        if (config.frequency?.time_window_days) setFrequencyWindow(config.frequency.time_window_days);
        if (config.monetary?.time_window_days)  setMonetaryWindow(config.monetary.time_window_days);

        // buckets — always set all 5 fixed rows, merging from API data
        setRecencyRanges(mergeScoreRanges(config.recency?.buckets));
        setFrequencyRanges(mergeScoreRanges(config.frequency?.buckets));
        setMonetaryRanges(mergeScoreRanges(config.monetary?.buckets));

        // segments — always use fixed set, merge rules/enabled from API by name
        setSegments(FIXED_SEGMENTS.map(fs => {
            const s = (config.segments || []).find(x => x.name === fs.name);
            return {
                name:    fs.name,
                color:   fs.color,
                enabled: s ? s.enabled !== false : true,
                rule: s ? {
                    recencyOperator:   operatorFromApi(s.conditions?.r?.operator || '>='),
                    recencyValue:      s.conditions?.r?.value ?? 4,
                    frequencyOperator: operatorFromApi(s.conditions?.f?.operator || '>='),
                    frequencyValue:    s.conditions?.f?.value ?? 4,
                    monetaryOperator:  operatorFromApi(s.conditions?.m?.operator || '>='),
                    monetaryValue:     s.conditions?.m?.value ?? 4,
                } : defaultSegmentRule(),
            };
        }));
    };

    // ── mount ───────────────────────────────────────────────────
    useEffect(() => {
        const fetchConfig = async () => {
            if (!routeId || routeId === 'new' || routeId === 'undefined') {
                setConfigLoading(false);
                return;
            }
            setConfigLoading(true);
            try {
                const res = await API.get(
                    `${process.env.REACT_APP_API_URL}/rfm/config?_id=${routeId}`,
                    { headers: getRfmHeaders() }
                );
                // by-ID returns { data: { event_config: {...} } }
                const config = res.data?.data?.event_config
                    || (res.data?.data?.event_configs || [])[0];
                if (res.data?.status && config) {
                    setLoadedConfig(config);
                    loadConfigFromApi(config);
                }
            } catch (e) {
                console.error('Failed to load RFM config:', e);
            } finally {
                setConfigLoading(false);
            }
        };
        fetchConfig();
        fetchUniqueEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId]);

    // re-map event objects once both availableEvents and loadedConfig are ready
    useEffect(() => {
        if (!availableEvents.length || !loadedConfig) return;
        const findEvent = (name) =>
            availableEvents.find(e => e.name === name) || { id: name, name, displayName: name, tags: [] };
        if (loadedConfig.recency?.events?.length)
            setRecencyEvents(loadedConfig.recency.events.map(findEvent));
        if (loadedConfig.frequency?.events?.length)
            setFrequencyEvents(loadedConfig.frequency.events.map(findEvent));
        if (loadedConfig.monetary?.events?.length) {
            setMonetaryEvents(loadedConfig.monetary.events.map(ev => ({ ...findEvent(ev.event), property: ev.property })));
            loadedConfig.monetary.events.forEach(ev => fetchEventProperties(ev.event));
        }
    }, [availableEvents, loadedConfig]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── payload builders ────────────────────────────────────────
    const buildEventConfigPayload = () => {
        // include pending monetary selection (selected but "+" not yet clicked)
        const pendingMonetary = selectedMonetaryEventName && selectedMonetaryProperty
            && !monetaryEvents.find(e => e.name === selectedMonetaryEventName)
            ? [{ event: selectedMonetaryEventName, property: selectedMonetaryProperty }]
            : [];

        return {
            name:              modelName,
            is_enabled:        isEnabled,
            refresh_frequency: refreshFrequency,
            recency:   { events: recencyEvents.map(e => e.name) },
            frequency: { events: frequencyEvents.map(e => e.name), count_type: countTypeToApi(frequencyCountType) },
            monetary:  {
                events: [
                    ...monetaryEvents.map(e => ({ event: e.name, property: e.property })),
                    ...pendingMonetary,
                ],
                aggregation: monetaryAggregation,
            },
        };
    };

    const buildRuleConfigPayload = () => ({
        recency:   { time_window_days: recencyWindow  || 30 },
        frequency: { time_window_days: frequencyWindow || 30, count_type: countTypeToApi(frequencyCountType) },
        monetary:  { time_window_days: monetaryWindow  || 30 },
        _id: rfmId,
    });

    const buildScoreConfigPayload = () => ({
        recency:   { buckets: bucketsToApi(recencyRanges) },
        frequency: { buckets: bucketsToApi(frequencyRanges) },
        monetary:  { buckets: bucketsToApi(monetaryRanges) },
        _id: rfmId,
    });

    const buildSegmentConfigPayload = () => ({
        segments: segments.map(s => ({
            name:    s.name,
            color:   chakraColorToHex(s.color),
            enabled: s.enabled,
            conditions: {
                r: { operator: operatorToApi(s.rule.recencyOperator),   value: s.rule.recencyValue },
                f: { operator: operatorToApi(s.rule.frequencyOperator), value: s.rule.frequencyValue },
                m: { operator: operatorToApi(s.rule.monetaryOperator),  value: s.rule.monetaryValue },
            },
        })),
        _id: rfmId,
    });

    // ── api actions ─────────────────────────────────────────────

    // Tab 1: POST /rfm/create-event-config  OR  PUT /rfm/update-event-config
    const isEditMode = !!(routeId && routeId !== 'new' && routeId !== 'undefined');

    /**
     * @param {object}  opts
     * @param {boolean} opts.silent  suppress the per-tab toast — handleSaveAll emits one
     *                               combined toast instead of one per endpoint.
     */
    const saveEventConfig = async ({ silent = false } = {}) => {
        if (!modelName.trim()) {
            toast({ title: 'Model name is required', status: 'warning', duration: 3000, isClosable: true });
            return null;
        }
        setTabSaving(prev => ({ ...prev, events: true }));
        try {
            const currentId = rfmId || routeId;
            const payload = isEditMode
                ? { ...buildEventConfigPayload(), _id: currentId }
                : buildEventConfigPayload();

            const res = isEditMode
                ? await API.put(`${process.env.REACT_APP_API_URL}/rfm/update-event-config`, payload, { headers: getRfmHeaders() })
                : await API.post(`${process.env.REACT_APP_API_URL}/rfm/create-event-config`, payload, { headers: getRfmHeaders() });

            if (res.data?.status) {
                const id = isEditMode
                    ? currentId
                    : (res.data.data?.event_config?._id || res.data.data?._id || res.data.data?.config?._id);
                if (id) {
                    setRfmId(id);
                    // RFM_004: this used to chain `await saveRuleConfig(id)`, which fired the
                    // rule-config PUT with whatever defaults were in state — persisting rules
                    // the user had never opened, let alone chosen — and produced a second
                    // success toast. Each tab now saves only itself; handleSaveAll decides
                    // which of the others the user actually touched.
                    if (!silent) {
                        toast({ title: isEditMode ? 'Event configuration updated' : 'Event configuration saved', status: 'success', duration: 2000, isClosable: true });
                    }
                    return id;
                }
            }
        } catch (error) {
            toast({
                title: 'Save Failed',
                description: error.response?.data?.message || error.message,
                status: 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setTabSaving(prev => ({ ...prev, events: false }));
        }
        return null;
    };

    // Tab 2: PUT /rfm/rule_config
    const saveRuleConfig = async (id = rfmId, { silent = false } = {}) => {
        if (!id) return;
        setTabSaving(prev => ({ ...prev, rules: true }));
        try {
            const res = await API.put(
                `${process.env.REACT_APP_API_URL}/rfm/rule_config`,
                { ...buildRuleConfigPayload(), _id: id },
                { headers: getRfmHeaders() }
            );
            if (res.data?.status && !silent)
                toast({ title: 'Rule configuration saved', status: 'success', duration: 2000, isClosable: true });
        } catch (error) {
            toast({
                title: 'Save Failed',
                description: error.response?.data?.message || error.message,
                status: 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setTabSaving(prev => ({ ...prev, rules: false }));
        }
    };

    // Tab 3: PUT /rfm/score_config
    const saveScoreConfig = async (id = rfmId, { silent = false } = {}) => {
        if (!id) return;
        setTabSaving(prev => ({ ...prev, scoring: true }));
        try {
            const res = await API.put(
                `${process.env.REACT_APP_API_URL}/rfm/score_config`,
                { ...buildScoreConfigPayload(), _id: id },
                { headers: getRfmHeaders() }
            );
            if (res.data?.status && !silent)
                toast({ title: 'Scoring model saved', status: 'success', duration: 2000, isClosable: true });
        } catch (error) {
            toast({
                title: 'Save Failed',
                description: error.response?.data?.message || error.message,
                status: 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setTabSaving(prev => ({ ...prev, scoring: false }));
        }
    };

    // Tab 4: PUT /rfm/segment_config
    const saveSegmentConfig = async (id = rfmId, { silent = false } = {}) => {
        if (!id) return;
        setTabSaving(prev => ({ ...prev, segments: true }));
        try {
            const res = await API.put(
                `${process.env.REACT_APP_API_URL}/rfm/segment_config`,
                { ...buildSegmentConfigPayload(), _id: id },
                { headers: getRfmHeaders() }
            );
            if (res.data?.status && !silent)
                toast({ title: 'Segment configuration saved', status: 'success', duration: 2000, isClosable: true });
        } catch (error) {
            toast({
                title: 'Save Failed',
                description: error.response?.data?.message || error.message,
                status: 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setTabSaving(prev => ({ ...prev, segments: false }));
        }
    };

    /**
     * Bottom "Save Configuration" — finish and leave.
     *
     * Previously it saved ONLY the tab that happened to be active and stayed on the page:
     *   • RFM_006 — a Frequency choice made on the Rule tab was lost if the user switched
     *     tabs before saving, because that tab's endpoint was never called.
     *   • RFM_001 — nothing navigated afterwards, so the operator had no signal the model
     *     had been created and never saw it in the list.
     *
     * Now: the event config always saves first (it owns the name and mints the id every
     * other endpoint needs), then each tab the user actually edited, then one toast and a
     * redirect to the list. The per-tab Save buttons remain for incremental work.
     */
    const handleSaveAll = async () => {
        setFullSaving(true);
        try {
            // Tab 0 always saves: it carries the model name, and on create it is what
            // produces the id. Silent — a single combined toast is emitted below.
            const id = await saveEventConfig({ silent: true });
            if (!id) return;   // validation failed or the request errored; toast already shown

            if (dirtyTabs.rules)    await saveRuleConfig(id, { silent: true });
            if (dirtyTabs.scoring)  await saveScoreConfig(id, { silent: true });
            if (dirtyTabs.segments) await saveSegmentConfig(id, { silent: true });

            toast({
                title: isEditMode ? 'Model updated' : 'Model created',
                description: `"${modelName}" has been saved.`,
                status: 'success', duration: 3000, isClosable: true,
            });
            navigate('/marketing/rfm-models');
        } finally {
            setFullSaving(false);
        }
    };

    // ── event handlers ──────────────────────────────────────────
    const handleAddRecencyEvent = (e) => {
        const name = e.target.value;
        if (!name) return;
        const ev = availableEvents.find(x => x.name === name);
        if (ev && !recencyEvents.find(x => x.name === name)) setRecencyEvents([...recencyEvents, ev]);
        e.target.value = '';
    };

    const handleAddFrequencyEvent = (e) => {
        const name = e.target.value;
        if (!name) return;
        const ev = availableEvents.find(x => x.name === name);
        if (ev && !frequencyEvents.find(x => x.name === name)) setFrequencyEvents([...frequencyEvents, ev]);
        e.target.value = '';
    };

    const handleMonetaryEventSelect = (e) => {
        const name = e.target.value;
        setSelectedMonetaryEventName(name);
        setSelectedMonetaryProperty('');
        if (name) fetchEventProperties(name);
    };

    const handleAddMonetaryEvent = () => {
        if (!selectedMonetaryEventName || !selectedMonetaryProperty) return;
        if (monetaryEvents.find(x => x.name === selectedMonetaryEventName)) return;
        const ev = availableEvents.find(x => x.name === selectedMonetaryEventName)
            || { id: selectedMonetaryEventName, name: selectedMonetaryEventName, displayName: selectedMonetaryEventName, tags: [] };
        setMonetaryEvents(prev => [...prev, { ...ev, property: selectedMonetaryProperty }]);
        setSelectedMonetaryEventName('');
        setSelectedMonetaryProperty('');
    };

    const handleRemoveEvent = (name, type) => {
        if (type === 'recency')   setRecencyEvents(recencyEvents.filter(e => e.name !== name));
        if (type === 'frequency') setFrequencyEvents(frequencyEvents.filter(e => e.name !== name));
        if (type === 'monetary')  setMonetaryEvents(monetaryEvents.filter(e => e.name !== name));
    };

    const updateSegmentRule = (index, field, value) => {
        markDirty('segments');
        const updated = [...segments];
        updated[index].rule = { ...updated[index].rule, [field]: value };
        setSegments(updated);
    };


    // Tab save button — disabled for tabs 2/3/4 until event config is created
    const TabSaveButton = ({ tabKey, label, onClick, requiresId = false }) => (
        !rfmModelConfigurePermission ? null :
        <Button
            size="sm"
            variant="outline"
            borderColor="var(--brand-primary)"
            color="var(--brand-primary)"
            _hover={{ bg: 'var(--brand-tint)' }}
            isLoading={!!tabSaving[tabKey]}
            loadingText="Saving..."
            isDisabled={requiresId && !rfmId}
            title={requiresId && !rfmId ? 'Save Event Configuration first' : ''}
            onClick={onClick}
        >
            {label || 'Save'}
        </Button>
    );

    // ── render ──────────────────────────────────────────────────
    if (configLoading || featurePermissionsLoading) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Center py={20}><Spinner size="xl" color="var(--brand-primary)" /></Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
                {/* Page header */}
                <Flex align="center" gap={2} mb={2}>
                    <Button leftIcon={<FiArrowLeft />} variant="ghost" size="sm" onClick={() => navigate('/marketing/rfm-models')}>
                        Back to Models
                    </Button>
                </Flex>
                <Box mb={6}>
                    <Text fontSize="2xl" fontWeight={600}>Configure RFM Model</Text>
                    <Text color="gray.600">Define how customer value is calculated using your event data</Text>
                </Box>

                {/* Model name — global field */}
                <Box className="box-style-card" mb={4}>
                    <FormControl isRequired>
                        <FormLabel fontSize="sm" fontWeight="medium">Model Name</FormLabel>
                        <Input
                            size="sm"
                            placeholder="e.g. Main RFM Model"
                            value={modelName}
                            onChange={e => setModelName(e.target.value)}
                            maxW="400px"
                        />
                    </FormControl>
                </Box>

                {!rfmId && (
                    <Box px={3} py={2.5} bg="var(--brand-tint)" border="1px solid var(--brand-border)" borderLeft="3px solid var(--brand-primary)" borderRadius="8px" mb={4}>
                        <HStack spacing={1.5} mb={1} align="center">
                            <Box color="var(--brand-primary)" flexShrink={0}><FiInfo size={13} /></Box>
                            <Text fontSize="xs" fontWeight={700} color="var(--brand-primary-active)">Getting started</Text>
                        </HStack>
                        <Text fontSize="xs" color="#3b82f6" lineHeight={1.7}>
                            Fill in the{' '}
                            <Text as="span" fontWeight={700} color="var(--brand-primary-hover)">Event Configuration</Text>
                            {' '}tab and save it first — this creates the model and unlocks the other tabs.
                        </Text>
                    </Box>
                )}

                <Tabs size="sm" sx={{ 'span.chakra-switch__track[data-checked]': { backgroundColor: 'var(--brand-primary)' } }} variant="enclosed" index={activeTab} onChange={setActiveTab}>
                    <TabList flexWrap="wrap">
                        <Tab>Event Configuration</Tab>
                        <Tab>Rule Configuration</Tab>
                        <Tab>Scoring Model</Tab>
                        <Tab>Segment Mapping</Tab>
                    </TabList>

                    <TabPanels>

                        {/* ── Tab 1: Event Configuration ─────────────────────────── */}
                        <TabPanel px={0}>
                            <Box className="box-style-card">
                                <Flex justify="space-between" align="center" mb={4}>
                                    <HStack>
                                        <Box color="var(--brand-primary)"><FiUsers size={18} /></Box>
                                        <Text fontSize="lg" fontWeight={600}>Event Configuration</Text>
                                    </HStack>
                                    <TabSaveButton tabKey="events" onClick={saveEventConfig} />
                                </Flex>

                                {/* is_enabled + refresh_frequency */}
                                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} mb={6}>
                                    <FormControl display="flex" alignItems="center" gap={3}>
                                        <FormLabel fontSize="sm" fontWeight="medium" mb={0}>Enable Model</FormLabel>
                                        <Switch size="sm" isChecked={isEnabled} sx={{ 'span.chakra-switch__track[data-checked]': { backgroundColor: 'var(--brand-primary)' } }} onChange={e => setIsEnabled(e.target.checked)} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="medium">Refresh Frequency</FormLabel>
                                        <Select value={refreshFrequency} onChange={e => setRefreshFrequency(e.target.value)} size="sm">
                                            <option value="hourly">Hourly</option>
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Text fontSize="sm" color="gray.500" mb={6}>Select which events contribute to each RFM dimension</Text>

                                <VStack align="stretch" spacing={6}>
                                    {/* Recency Events */}
                                    <Box>
                                        <HStack mb={3}>
                                            <Box w={8} h={8} bg="var(--brand-tint-strong)" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                                                <Box color="#155dfc"><FiClock /></Box>
                                            </Box>
                                            <Box>
                                                <Text fontWeight="medium">Recency Events</Text>
                                                <Text fontSize="xs" color="gray.500">Track when customers last engaged</Text>
                                            </Box>
                                        </HStack>
                                        <VStack align="stretch" spacing={2} pl={10}>
                                            {recencyEvents.map(ev => (
                                                <EventItem key={ev.name} event={ev} onRemove={() => handleRemoveEvent(ev.name, 'recency')} />
                                            ))}
                                            <Select size="sm"
                                                placeholder={loadingEvents ? 'Loading events...' : 'Select event to add'}
                                                onChange={handleAddRecencyEvent}
                                                isDisabled={loadingEvents}
                                            >
                                                {availableEvents
                                                    .filter(e => !recencyEvents.find(r => r.name === e.name))
                                                    .map(e => <option key={e.name} value={e.name}>{e.displayName}</option>)}
                                            </Select>
                                            {loadingEvents && <HStack><Spinner size="xs" /><Text fontSize="xs" color="gray.500">Loading events...</Text></HStack>}
                                        </VStack>
                                    </Box>

                                    {/* Frequency Events */}
                                    <Box>
                                        <HStack mb={3}>
                                            <Box w={8} h={8} bg="#f3e8ff" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                                                <Box color="#9810fa"><FiRefreshCw /></Box>
                                            </Box>
                                            <Box>
                                                <Text fontWeight="medium">Frequency Events</Text>
                                                <Text fontSize="xs" color="gray.500">Measure customer engagement level</Text>
                                            </Box>
                                        </HStack>
                                        <VStack align="stretch" spacing={2} pl={10}>
                                            {frequencyEvents.map(ev => (
                                                <EventItem key={ev.name} event={ev} onRemove={() => handleRemoveEvent(ev.name, 'frequency')} />
                                            ))}
                                            <Select size="sm"
                                                placeholder={loadingEvents ? 'Loading events...' : 'Select event to add'}
                                                onChange={handleAddFrequencyEvent}
                                                isDisabled={loadingEvents}
                                            >
                                                {availableEvents
                                                    .filter(e => !frequencyEvents.find(f => f.name === e.name))
                                                    .map(e => <option key={e.name} value={e.name}>{e.displayName}</option>)}
                                            </Select>
                                            {loadingEvents && <HStack><Spinner size="xs" /><Text fontSize="xs" color="gray.500">Loading events...</Text></HStack>}
                                        </VStack>
                                    </Box>

                                    {/* Monetary Events */}
                                    <Box>
                                        <HStack mb={3}>
                                            <Box w={8} h={8} bg="var(--brand-tint)" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                                                <Box color="var(--brand-primary)"><FiDollarSign /></Box>
                                            </Box>
                                            <Box>
                                                <Text fontWeight="medium">Monetary Events</Text>
                                                <Text fontSize="xs" color="gray.500">Calculate customer spend value</Text>
                                            </Box>
                                        </HStack>
                                        <VStack align="stretch" spacing={2} pl={10}>
                                            {monetaryEvents.map(ev => (
                                                <EventItem key={ev.name} event={ev} onRemove={() => handleRemoveEvent(ev.name, 'monetary')} />
                                            ))}
                                            <HStack spacing={2}>
                                                <Select size="sm"
                                                    placeholder={loadingEvents ? 'Loading events...' : 'Select event'}
                                                    value={selectedMonetaryEventName}
                                                    onChange={handleMonetaryEventSelect}
                                                    isDisabled={loadingEvents}
                                                >
                                                    {availableEvents
                                                        .filter(e => !monetaryEvents.find(m => m.name === e.name))
                                                        .map(e => <option key={e.name} value={e.name}>{e.displayName}</option>)}
                                                </Select>
                                                <Select size="sm"
                                                    placeholder={!selectedMonetaryEventName ? 'Select event first' : loadingProperties ? 'Loading...' : 'Select property'}
                                                    value={selectedMonetaryProperty}
                                                    onChange={e => setSelectedMonetaryProperty(e.target.value)}
                                                    isDisabled={!selectedMonetaryEventName || loadingProperties}
                                                >
                                                    {(eventPropertiesCache[selectedMonetaryEventName] || []).map(p => (
                                                        <option key={p} value={p}>{p}</option>
                                                    ))}
                                                </Select>
                                                <IconButton icon={<FiPlus />} size="sm" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} aria-label="Add"
                                                    isDisabled={!selectedMonetaryEventName || !selectedMonetaryProperty}
                                                    onClick={handleAddMonetaryEvent} />
                                            </HStack>
                                            {loadingProperties && selectedMonetaryEventName && (
                                                <HStack><Spinner size="xs" /><Text fontSize="xs" color="gray.500">Loading properties...</Text></HStack>
                                            )}
                                            {/* Aggregation */}
                                            <Box pt={1}>
                                                <Text fontSize="sm" mb={2} fontWeight="medium">Aggregation Method</Text>
                                                <HStack spacing={2}>
                                                    {['sum', 'avg', 'max'].map(agg => (
                                                        <Button key={agg} size="sm"
                                                            bg={monetaryAggregation === agg ? 'var(--brand-primary)' : 'white'}
                                                            color={monetaryAggregation === agg ? 'white' : 'gray.500'}
                                                            border="1px solid"
                                                            borderColor={monetaryAggregation === agg ? 'var(--brand-primary)' : 'gray.200'}
                                                            _hover={{ bg: monetaryAggregation === agg ? 'var(--brand-primary-hover)' : 'gray.50', borderColor: 'var(--brand-primary)', color: monetaryAggregation === agg ? 'white' : 'var(--brand-primary)' }}
                                                            onClick={() => dirty('rules', setMonetaryAggregation)(agg)}>
                                                            {agg.charAt(0).toUpperCase() + agg.slice(1)}
                                                        </Button>
                                                    ))}
                                                </HStack>
                                            </Box>
                                        </VStack>
                                    </Box>
                                </VStack>
                            </Box>
                        </TabPanel>

                        {/* ── Tab 2: Rule Configuration ──────────────────────────── */}
                        <TabPanel px={0}>
                            <Box className="box-style-card">
                                <Flex justify="space-between" align="center" mb={6}>
                                    <HStack>
                                        <Box color="var(--brand-primary)"><FiTrendingUp size={18} /></Box>
                                        <Text fontSize="lg" fontWeight={600}>Rule Configuration</Text>
                                    </HStack>
                                    <TabSaveButton tabKey="rules" requiresId onClick={() => saveRuleConfig()} />
                                </Flex>

                                {!rfmId && (
                                    <Box px={3} py={2.5} bg="var(--brand-tint)" border="1px solid var(--brand-border)" borderLeft="3px solid var(--brand-primary)" borderRadius="8px" mb={4}>
                                        <HStack spacing={1.5} mb={1} align="center">
                                            <Box color="var(--brand-primary)" flexShrink={0}><FiInfo size={13} /></Box>
                                            <Text fontSize="xs" fontWeight={700} color="var(--brand-primary-active)">Step required</Text>
                                        </HStack>
                                        <Text fontSize="xs" color="#3b82f6" lineHeight={1.7}>
                                            Save the{' '}
                                            <Text as="span" fontWeight={700} color="var(--brand-primary-hover)">Event Configuration</Text>
                                            {' '}tab first to unlock this section.
                                        </Text>
                                    </Box>
                                )}

                                <VStack align="stretch" spacing={6}>
                                    <Box>
                                        <HStack mb={3}><Box color="blue.600"><FiClock /></Box><Text fontWeight="medium">Recency</Text></HStack>
                                        <HStack spacing={2} flexWrap="wrap" pl={6}>
                                            {[7, 15, 30, 60, 90, 180, 365].map(d => (
                                                <Button key={d} size="xs"
                                                    bg={recencyWindow === d ? 'var(--brand-primary)' : 'white'}
                                                    color={recencyWindow === d ? 'white' : 'gray.500'}
                                                    border="1px solid"
                                                    borderColor={recencyWindow === d ? 'var(--brand-primary)' : 'gray.200'}
                                                    _hover={{ bg: recencyWindow === d ? 'var(--brand-primary-hover)' : 'gray.50', borderColor: 'var(--brand-primary)', color: recencyWindow === d ? 'white' : 'var(--brand-primary)' }}
                                                    onClick={() => dirty('rules', setRecencyWindow)(d)}>
                                                    {d} days
                                                </Button>
                                            ))}
                                        </HStack>
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <HStack mb={3}><Box color="purple.600"><FiRefreshCw /></Box><Text fontWeight="medium">Frequency</Text></HStack>
                                        <VStack align="stretch" spacing={3} pl={6}>
                                            <Box>
                                                <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={2}>Time Window</Text>
                                                <HStack spacing={2} flexWrap="wrap">
                                                    {[7, 15, 30, 60, 90, 180, 365].map(d => (
                                                        <Button key={d} size="xs"
                                                            bg={frequencyWindow === d ? 'var(--brand-primary)' : 'white'}
                                                            color={frequencyWindow === d ? 'white' : 'gray.500'}
                                                            border="1px solid"
                                                            borderColor={frequencyWindow === d ? 'var(--brand-primary)' : 'gray.200'}
                                                            _hover={{ bg: frequencyWindow === d ? 'var(--brand-primary-hover)' : 'gray.50', borderColor: 'var(--brand-primary)', color: frequencyWindow === d ? 'white' : 'var(--brand-primary)' }}
                                                            onClick={() => dirty('rules', setFrequencyWindow)(d)}>
                                                            {d} days
                                                        </Button>
                                                    ))}
                                                </HStack>
                                            </Box>
                                            <Box>
                                                <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={2}>Count Type</Text>
                                                <HStack spacing={2}>
                                                    {[
                                                        { val: 'total',  label: 'Total Events' },
                                                        { val: 'unique', label: 'Unique Active Days' },
                                                    ].map(({ val, label }) => (
                                                        <Button key={val} size="xs"
                                                            bg={frequencyCountType === val ? 'var(--brand-primary)' : 'white'}
                                                            color={frequencyCountType === val ? 'white' : 'gray.500'}
                                                            border="1px solid"
                                                            borderColor={frequencyCountType === val ? 'var(--brand-primary)' : 'gray.200'}
                                                            _hover={{ bg: frequencyCountType === val ? 'var(--brand-primary-hover)' : 'gray.50', borderColor: 'var(--brand-primary)', color: frequencyCountType === val ? 'white' : 'var(--brand-primary)' }}
                                                            onClick={() => dirty('rules', setFrequencyCountType)(val)}>
                                                            {label}
                                                        </Button>
                                                    ))}
                                                </HStack>
                                            </Box>
                                        </VStack>
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <HStack mb={3}><Box color="green.600"><FiDollarSign /></Box><Text fontWeight="medium">Monetary</Text></HStack>
                                        <HStack spacing={2} flexWrap="wrap" pl={6}>
                                            {[7, 15, 30, 60, 90, 180, 365, 395].map(d => (
                                                <Button key={d} size="xs"
                                                    bg={monetaryWindow === d ? 'var(--brand-primary)' : 'white'}
                                                    color={monetaryWindow === d ? 'white' : 'gray.500'}
                                                    border="1px solid"
                                                    borderColor={monetaryWindow === d ? 'var(--brand-primary)' : 'gray.200'}
                                                    _hover={{ bg: monetaryWindow === d ? 'var(--brand-primary-hover)' : 'gray.50', borderColor: 'var(--brand-primary)', color: monetaryWindow === d ? 'white' : 'var(--brand-primary)' }}
                                                    onClick={() => dirty('rules', setMonetaryWindow)(d)}>
                                                    {d} days
                                                </Button>
                                            ))}
                                        </HStack>
                                    </Box>
                                </VStack>
                            </Box>
                        </TabPanel>

                        {/* ── Tab 3: Scoring Model ───────────────────────────────── */}
                        <TabPanel px={0}>
                            <Box className="box-style-card">
                                <Flex justify="space-between" align="center" mb={2}>
                                    <HStack>
                                        <Box color="var(--brand-primary)"><FiAlertCircle size={18} /></Box>
                                        <Text fontSize="lg" fontWeight={600}>Scoring Model</Text>
                                    </HStack>
                                    <TabSaveButton tabKey="scoring" requiresId onClick={() => saveScoreConfig()} />
                                </Flex>

                                {!rfmId && (
                                    <Box px={3} py={2.5} bg="var(--brand-tint)" border="1px solid var(--brand-border)" borderLeft="3px solid var(--brand-primary)" borderRadius="8px" mb={4}>
                                        <HStack spacing={1.5} mb={1} align="center">
                                            <Box color="var(--brand-primary)" flexShrink={0}><FiInfo size={13} /></Box>
                                            <Text fontSize="xs" fontWeight={700} color="var(--brand-primary-active)">Step required</Text>
                                        </HStack>
                                        <Text fontSize="xs" color="#3b82f6" lineHeight={1.7}>
                                            Save the{' '}
                                            <Text as="span" fontWeight={700} color="var(--brand-primary-hover)">Event Configuration</Text>
                                            {' '}tab first to unlock this section.
                                        </Text>
                                    </Box>
                                )}

                                <Text fontSize="sm" color="gray.500" mb={6}>Define score buckets (1-5) for each dimension. Leave "To" blank for open-ended.</Text>
                                <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={6}>
                                    <Box>
                                        <HStack mb={3}><Box color="blue.600"><FiClock size={14} /></Box><Text fontSize="sm" fontWeight="medium">Recency Buckets (days)</Text></HStack>
                                        <ScoreRangeTable ranges={recencyRanges} setRanges={dirty('scoring', setRecencyRanges)} colorScheme="var(--brand-tint-strong)" />
                                    </Box>
                                    <Box>
                                        <HStack mb={3}><Box color="purple.600"><FiRefreshCw size={14} /></Box><Text fontSize="sm" fontWeight="medium">Frequency Buckets (event count)</Text></HStack>
                                        <ScoreRangeTable ranges={frequencyRanges} setRanges={dirty('scoring', setFrequencyRanges)} colorScheme="#f3e8ff" />
                                    </Box>
                                    <Box>
                                        <HStack mb={3}><Box color="green.600"><FiDollarSign size={14} /></Box><Text fontSize="sm" fontWeight="medium">Monetary Buckets (amount)</Text></HStack>
                                        <ScoreRangeTable ranges={monetaryRanges} setRanges={dirty('scoring', setMonetaryRanges)} colorScheme="#dcfce7" />
                                    </Box>
                                </Grid>
                            </Box>
                        </TabPanel>

                        {/* ── Tab 4: Segment Mapping ─────────────────────────────── */}
                        <TabPanel px={0}>
                            <Box className="box-style-card">
                                <Flex justify="space-between" align="center" mb={2}>
                                    <HStack>
                                        <Box color="var(--brand-primary)"><FiUsers size={18} /></Box>
                                        <Text fontSize="lg" fontWeight={600}>Segment Mapping</Text>
                                    </HStack>
                                    <TabSaveButton tabKey="segments" requiresId onClick={() => saveSegmentConfig()} />
                                </Flex>

                                {!rfmId && (
                                    <Box px={3} py={2.5} bg="var(--brand-tint)" border="1px solid var(--brand-border)" borderLeft="3px solid var(--brand-primary)" borderRadius="8px" mb={4}>
                                        <HStack spacing={1.5} mb={1} align="center">
                                            <Box color="var(--brand-primary)" flexShrink={0}><FiInfo size={13} /></Box>
                                            <Text fontSize="xs" fontWeight={700} color="var(--brand-primary-active)">Step required</Text>
                                        </HStack>
                                        <Text fontSize="xs" color="#3b82f6" lineHeight={1.7}>
                                            Save the{' '}
                                            <Text as="span" fontWeight={700} color="var(--brand-primary-hover)">Event Configuration</Text>
                                            {' '}tab first to unlock this section.
                                        </Text>
                                    </Box>
                                )}

                                <Text fontSize="sm" color="gray.500" mb={6}>Define customer segments based on RFM scores</Text>
                                <VStack align="stretch" spacing={4}>
                                    {segments.map((segment, index) => (
                                        <Box key={segment.name} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                                            <Flex justify="space-between" align="center" mb={3}>
                                                <HStack spacing={3}>
                                                    <Box w={3} h={3} borderRadius="full" bg={segment.color} flexShrink={0} />
                                                    <Text fontWeight="semibold" fontSize="sm">{segment.name}</Text>
                                                </HStack>
                                                <Switch size="sm" isChecked={segment.enabled} sx={{ 'span.chakra-switch__track[data-checked]': { backgroundColor: 'var(--brand-primary)' } }}
                                                    onChange={e => {
                                                        markDirty('segments');
                                                        const s = [...segments];
                                                        s[index].enabled = e.target.checked;
                                                        setSegments(s);
                                                    }} />
                                            </Flex>
                                            <Flex align="center" gap={2} bg="white" px={3} py={2} borderRadius="md" border="1px solid" borderColor="gray.200" flexWrap="wrap">
                                                <Text fontSize="sm">Recency</Text>
                                                <Select size="xs" w="60px" value={segment.rule.recencyOperator} onChange={e => updateSegmentRule(index, 'recencyOperator', e.target.value)}>
                                                    <option value="≥">≥</option><option value="≤">≤</option><option value="=">=</option>
                                                </Select>
                                                <Select size="xs" w="55px" value={segment.rule.recencyValue} onChange={e => updateSegmentRule(index, 'recencyValue', parseInt(e.target.value))}>
                                                    {[1,2,3,4,5].map(s => <option key={s} value={s}>{s}</option>)}
                                                </Select>
                                                <Text fontSize="sm" color="gray.400">AND</Text>
                                                <Text fontSize="sm">Frequency</Text>
                                                <Select size="xs" w="60px" value={segment.rule.frequencyOperator} onChange={e => updateSegmentRule(index, 'frequencyOperator', e.target.value)}>
                                                    <option value="≥">≥</option><option value="≤">≤</option><option value="=">=</option>
                                                </Select>
                                                <Select size="xs" w="55px" value={segment.rule.frequencyValue} onChange={e => updateSegmentRule(index, 'frequencyValue', parseInt(e.target.value))}>
                                                    {[1,2,3,4,5].map(s => <option key={s} value={s}>{s}</option>)}
                                                </Select>
                                                <Text fontSize="sm" color="gray.400">AND</Text>
                                                <Text fontSize="sm">Monetary</Text>
                                                <Select size="xs" w="60px" value={segment.rule.monetaryOperator} onChange={e => updateSegmentRule(index, 'monetaryOperator', e.target.value)}>
                                                    <option value="≥">≥</option><option value="≤">≤</option><option value="=">=</option>
                                                </Select>
                                                <Select size="xs" w="55px" value={segment.rule.monetaryValue} onChange={e => updateSegmentRule(index, 'monetaryValue', parseInt(e.target.value))}>
                                                    {[1,2,3,4,5].map(s => <option key={s} value={s}>{s}</option>)}
                                                </Select>
                                            </Flex>
                                        </Box>
                                    ))}
                                </VStack>
                            </Box>
                        </TabPanel>

                    </TabPanels>
                </Tabs>

                {/* Footer */}
                <Flex justify="flex-end" gap={3} mt={6}>
                    <Button size="sm" variant="outline" onClick={() => navigate('/marketing/rfm-models')}>Cancel</Button>
                    {rfmModelConfigurePermission && (
                        <Button size="sm" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} _active={{ bg: 'var(--brand-primary-active)' }} onClick={handleSaveAll} isLoading={fullSaving} loadingText="Saving...">
                            Save Configuration
                        </Button>
                    )}
                </Flex>
            </BoxWrapper>
        </>
    );
};

export default RFMConfiguration;
